/** P32 (Feb 27, 2026) — HCPCS variant / payer / service-line resolution
 *  helpers for the Rhinobill claim page.
 *
 *  These mirror backend/config/modifier_catalog logic so the UI can
 *  preview which chips will be active + which modifier(s) will be
 *  auto-applied before the user submits. The backend always re-validates.
 */
import {
  ASSESSMENT_CODES_FRONTEND,
  HCPCS_VARIANTS_FRONTEND,
} from '../constants';

/** Resolve a variant key (e.g. "T1000-RN") to the underlying HCPCS ("T1000").
 *  Pass-through for non-variant codes. */
export function resolveHcpcsFromVariant(variantOrCode) {
  const key = (variantOrCode || '').trim().toUpperCase();
  for (const [hcpcs, variants] of Object.entries(HCPCS_VARIANTS_FRONTEND)) {
    if (variants.some((v) => v.key === key)) return hcpcs;
  }
  return key;
}

/** Always-on modifiers for a variant (e.g. T1000-RN → ["TD"]). */
export function alwaysApplyFor(variantOrCode) {
  const key = (variantOrCode || '').trim().toUpperCase();
  for (const variants of Object.values(HCPCS_VARIANTS_FRONTEND)) {
    const m = variants.find((v) => v.key === key);
    if (m) return m.always_apply || [];
  }
  return [];
}

/** Max units a code may bill (assessment codes → 1, everything else → null). */
export function maxUnitsFor(variantOrCode) {
  const code = resolveHcpcsFromVariant(variantOrCode);
  return ASSESSMENT_CODES_FRONTEND[code] || null;
}

/** Compute the visit-sequence-or-duration modifier expected for the Nth
 *  visit of duration H hours. Used in the preview rendering. */
export function previewModifier(visitNumberInDay, durationHours) {
  if (durationHours > 12) return 'U4';
  if (visitNumberInDay >= 3) return 'U3';
  if (visitNumberInDay === 2) return 'U2';
  return '';
}

/** Build a friendly payer line from a patient profile.
 *  Example: "Aetna Better Health · MyCare" / "ODM · State Plan". */
export function friendlyPayerDisplay(patient) {
  if (!patient || !patient.billing_lines || !patient.billing_lines.length) return '';
  const line = patient.billing_lines[0];
  const insName = patient.insurance_name || line.payer_program || '';
  const payerType = line.payer_type || '';
  if (insName && payerType) return `${insName} · ${payerType}`;
  return insName || payerType || '';
}

/** Compute distinct payer tuples from a patient's billing_lines.
 *  Each tuple → one "payer option" the biller can pick. */
export function distinctPayerOptions(patient) {
  if (!patient || !patient.billing_lines) return [];
  const seen = new Set();
  const out = [];
  for (const line of patient.billing_lines) {
    const key = `${line.payer_type || ''}|${line.payer_program || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      key,
      payer_type: line.payer_type || '',
      payer_program: line.payer_program || '',
      label: line.payer_program
        ? `${line.payer_type || '?'} · ${line.payer_program}`
        : (line.payer_type || 'Unknown'),
    });
  }
  return out;
}

/** Filter billing_lines for the selected payer, return distinct service options.
 *  Expands T1000 into T1000-RN and T1000-LPN variants. */
export function servicesForPayer(patient, payerKey) {
  if (!patient || !patient.billing_lines) return [];
  const seen = new Set();
  const out = [];
  for (const line of patient.billing_lines) {
    const key = `${line.payer_type || ''}|${line.payer_program || ''}`;
    if (payerKey && key !== payerKey) continue;
    const code = (line.procedure_code || '').toUpperCase();
    if (!code) continue;
    // For codes with variants, expand into one entry per variant.
    const variants = HCPCS_VARIANTS_FRONTEND[code];
    if (variants && variants.length) {
      for (const v of variants) {
        if (seen.has(v.key)) continue;
        seen.add(v.key);
        out.push({
          code,
          variant_key: v.key,
          label: v.label,
          payer_type: line.payer_type,
          payer_program: line.payer_program,
        });
      }
    } else {
      if (seen.has(code)) continue;
      seen.add(code);
      out.push({
        code,
        variant_key: null,
        label: null,
        payer_type: line.payer_type,
        payer_program: line.payer_program,
      });
    }
  }
  return out;
}
